export const USER_TYPES = {
  SELLER: "Seller",
  SUPER_ADMIN: "Super Admin",
  PRINT_PROVIDER: "PrintProvider",
  DEVELOPER: "Developer",
  ADMIN_ACCESS_GUARD: "AdminAccessGuard",
  VIPS_ICP: "VIPS ICP",
  VIPS_PARTNER: "VIPS Partner",
};

export const VIPS_USER_TYPES = [USER_TYPES.VIPS_ICP, USER_TYPES.VIPS_PARTNER];

export const VIPS_ALLOWED_PATHS = [
  "/dashboard/orders",
  "/dashboard/settings",
  "/dashboard/profile",
];

const USER_TYPE_ALIASES = {
  "vips icp": USER_TYPES.VIPS_ICP,
  "vips partner": USER_TYPES.VIPS_PARTNER,
  vipsicp: USER_TYPES.VIPS_ICP,
  vipspartner: USER_TYPES.VIPS_PARTNER,
};

export const normalizeUserType = (userType) => {
  if (!userType) return userType;

  const trimmed = String(userType).trim();
  const alias = USER_TYPE_ALIASES[trimmed.toLowerCase().replace(/\s+/g, " ")];
  const compactAlias =
    USER_TYPE_ALIASES[trimmed.toLowerCase().replace(/\s+/g, "")];

  return alias || compactAlias || trimmed;
};

export const isVipsUserType = (userType) =>
  VIPS_USER_TYPES.includes(normalizeUserType(userType));

export const isVipsPartner = (userType) =>
  normalizeUserType(userType) === USER_TYPES.VIPS_PARTNER;

export const isSeller = (userType) =>
  normalizeUserType(userType) === USER_TYPES.SELLER;

export const isSuperAdmin = (userType) =>
  normalizeUserType(userType) === USER_TYPES.SUPER_ADMIN;

export const isPrintProvider = (userType) =>
  normalizeUserType(userType) === USER_TYPES.PRINT_PROVIDER;

export const isAdminRole = (userType) =>
  isSuperAdmin(userType) || isPrintProvider(userType);

export const isVipsWorkflowViewer = (userType) =>
  isVipsUserType(userType) || isPrintProvider(userType);

export const isVipsApprovedOrder = (order) =>
  order?.admin_approved === 1 || order?.admin_approved === "1";

export const isVipsApprovedFlag = (order) =>
  order?.vips_approved === 1 || order?.vips_approved === "1";

export const isOrderApprovalStatus = (orderStatus) =>
  orderStatus === "on hold" ||
  orderStatus === "awaiting approval" ||
  orderStatus === "awaiting vips approval";

export const isVipsWorkflowStatus = (orderStatus) =>
  orderStatus === "awaiting vips approval" || orderStatus === "remake";

export const isVipsOrdersSelectionStatus = (orderStatus) =>
  isVipsWorkflowStatus(orderStatus);

export const canApproveOrdersSelection = (orderStatus, userType) =>
  isVipsUserType(userType)
    ? isVipsOrdersSelectionStatus(orderStatus)
    : orderStatus === "on hold" ||
      orderStatus === "awaiting approval" ||
      orderStatus === "awaiting vips approval" ||
      orderStatus === "remake";

export const canDownloadOrdersSelection = (orderStatus, userType) => {
  if (
    isAdminRole(userType) &&
    (orderStatus === "awaiting shipment" || orderStatus === "shipped")
  ) {
    return true;
  }

  return (
    isSuperAdmin(userType) && orderStatus === "awaiting vips approval"
  );
};

export const hasOrderSelectionActions = (orderStatus, userType) =>
  canApproveOrdersSelection(orderStatus, userType) ||
  canDownloadOrdersSelection(orderStatus, userType);

export const canApproveRemakeOrders = (orderStatus, userType) =>
  orderStatus === "remake" &&
  (isVipsUserType(userType) || isAdminRole(userType));

export const canManageVipsNotes = (userType, orderStatus) =>
  isVipsWorkflowStatus(orderStatus) && isAdminRole(userType);

const isRefundedOrderContext = (orderStatus, order) => {
  const normalizedStatus = String(orderStatus || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, " ");

  if (normalizedStatus === "refunded") return true;

  if (
    order?.is_refunded === 1 ||
    order?.is_refunded === "1" ||
    order?.is_refunded === true
  ) {
    return true;
  }

  const orderStatusTag = String(
    order?.status_tag || order?.status || order?.order_status || "",
  )
    .trim()
    .toLowerCase()
    .replace(/_/g, " ");

  return orderStatusTag === "refunded";
};

export const canShowRefundAction = (userType, orderStatus, order) => {
  if (isPrintProvider(userType) || isVipsUserType(userType)) {
    return false;
  }

  if (isRefundedOrderContext(orderStatus, order)) {
    return false;
  }

  if (isSuperAdmin(userType)) {
    return true;
  }

  return (
    orderStatus === "awaiting approval" ||
    orderStatus === "awaiting shipment" ||
    (orderStatus === "in production" && order?.admin_approved === 0) ||
    orderStatus === "shipped"
  );
};

export const canRemakeOrderItem = (userType, orderStatus) =>
  orderStatus === "awaiting vips approval" && isVipsUserType(userType);

export const canEditRemakeFeedback = (userType, orderStatus) => {
  if (isPrintProvider(userType)) return false;

  if (isVipsUserType(userType)) {
    return (
      orderStatus === "remake" || orderStatus === "awaiting vips approval"
    );
  }

  if (isSuperAdmin(userType)) {
    return orderStatus === "shipped";
  }

  return false;
};

export const shouldShowRemakeFeedbackColumn = (userType, orderStatus) => {
  if (isPrintProvider(userType) || isSeller(userType)) return false;

  if (orderStatus === "remake") return true;

  return canEditRemakeFeedback(userType, orderStatus);
};

export const isApproveButtonStatus = (orderStatus, userType) =>
  isOrderApprovalStatus(orderStatus) ||
  canApproveRemakeOrders(orderStatus, userType);

export const shouldRedirectToOrdersOnLogin = (userType) =>
  isVipsUserType(userType) || isPrintProvider(userType);
